/**
 * Store module para FeatureCommander
 * Maneja el carrito de productos por reserva y los overlays
 */

import { normalizeChoices, arraysAreEqualIgnoringOrder } from "@/utils";

const state = {
	// Array de carritos por reserva
	// Cada elemento tiene: { id: reservationId, products: [...] }
	accountCarts: [],

	// Estados de overlays
	isShowOverlayFeatureCommander: false,
	isShowOverlayFeatureProductCommander: false,
	isShowOverlayFeatureCart: false,
	isShowOverlayFeatureEditProductList: false,
	isShowOverlayFeatureBreakfastCommander: false,
	isShowOverlayFeatureMenuCommander: false,

	// Producto en edición (para modo edición)
	editingProduct: null,

	// Indica si la edición viene del carrito (para volver al carrito al cerrar)
	editingFromCart: false,
};

const getters = {
	// Obtener todos los carritos
	accountCarts: (state) => state.accountCarts,

	// Getters de overlays
	getIsShowOverlayFeatureCommander: (state) => state.isShowOverlayFeatureCommander,
	getIsShowOverlayFeatureProductCommander: (state) => state.isShowOverlayFeatureProductCommander,
	getIsShowOverlayFeatureCart: (state) => state.isShowOverlayFeatureCart,
	getIsShowOverlayFeatureEditProductList: (state) => state.isShowOverlayFeatureEditProductList,
	getIsShowOverlayFeatureBreakfastCommander: (state) => state.isShowOverlayFeatureBreakfastCommander,
	getIsShowOverlayFeatureMenuCommander: (state) => state.isShowOverlayFeatureMenuCommander,

	// Producto en edición
	getEditingProduct: (state) => state.editingProduct,

	// Si la edición viene del carrito
	getEditingFromCart: (state) => state.editingFromCart,

	// Obtener carrito por ID de reserva
	getCartByReservationId: (state) => (reservationId) => {
		return state.accountCarts.find((cart) => cart.id === reservationId) || null;
	},

	// Obtener productos de una reserva
	getProductsByReservationId: (state) => (reservationId) => {
		const cart = state.accountCarts.find((cart) => cart.id === reservationId);
		return cart ? cart.products : [];
	},

	// Contar productos totales de una reserva
	getTotalProductsByReservationId: (state) => (reservationId) => {
		const cart = state.accountCarts.find((cart) => cart.id === reservationId);
		return cart ? cart.products.length : 0;
	},

	// Contar todos los productos de todos los carritos
	getTotalProducts: (state) => {
		return state.accountCarts.reduce((total, cart) => total + cart.products.length, 0);
	},
};

const mutations = {
	// Inicializar carrito para una reserva
	initializeCart(state, reservationId) {
		const existingCart = state.accountCarts.find((cart) => cart.id === reservationId);
		if (!existingCart) {
			state.accountCarts.push({
				id: reservationId,
				products: [],
			});
		}
	},

	// Agregar producto al carrito
	// Para productos unitarios: incrementa cartItemCount si ya existe uno con las mismas opciones
	// Para productos normales (bebidas, etc.): cada uno es independiente para poder editarlos/comentarlos
	addProductToCart(state, { reservationId, product }) {
		let cart = state.accountCarts.find((cart) => cart.id === reservationId);

		// Si no existe el carrito, crearlo
		if (!cart) {
			cart = {
				id: reservationId,
				products: [],
			};
			state.accountCarts.push(cart);
		}

		// Solo agrupar productos unitarios (isUnitary === 'SI')
		// Los demás productos se agregan individualmente para poder editarlos/comentarlos por separado
		if (product.isUnitary === "SI") {
			const normalizedOptions = normalizeChoices(product.chosenOptions);

			// Buscar si ya existe un producto unitario con el mismo SKU y opciones
			const existingProduct = cart.products.find((p) => {
				if (p.sku !== product.sku) return false;
				if (p.isUnitary !== "SI") return false;
				return arraysAreEqualIgnoringOrder(normalizeChoices(p.chosenOptions), normalizedOptions);
			});

			if (existingProduct) {
				// Incrementar unidades
				existingProduct.cartItemCount += product.unitaryIncrement || product.cartItemCount || 1;
				existingProduct.timestamp = Date.now();
				return;
			}
		}

		// Para productos normales o unitarios nuevos: crear entrada individual
		const productToAdd = {
			...product,
			timestamp: Date.now(),
			cartItemId: `${product.sku}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
			// Asegurar que cartItemCount esté definido
			cartItemCount: product.cartItemCount || 1,
		};
		cart.products.push(productToAdd);
	},

	// Eliminar producto del carrito por cartItemId
	removeProductFromCart(state, { reservationId, cartItemId }) {
		const cart = state.accountCarts.find((cart) => cart.id === reservationId);
		if (cart) {
			const index = cart.products.findIndex((p) => p.cartItemId === cartItemId);
			if (index !== -1) {
				cart.products.splice(index, 1);
			}
		}
	},

	// Quitar una unidad del producto (o eliminar si llega al mínimo)
	subtractUnit(state, { reservationId, cartItemId }) {
		const cart = state.accountCarts.find((cart) => cart.id === reservationId);
		if (!cart) return;

		const product = cart.products.find((p) => p.cartItemId === cartItemId);
		if (!product) return;

		// Determinar el mínimo según si es unitario o no
		const minCount = product.isUnitary === "SI" ? product.servingQtyMin || 1 : 1;

		if (product.cartItemCount <= minCount) {
			// Si está en el mínimo, eliminar el producto
			const index = cart.products.findIndex((p) => p.cartItemId === cartItemId);
			if (index !== -1) {
				cart.products.splice(index, 1);
			}
		} else {
			// Decrementar en 1
			product.cartItemCount -= 1;
			product.timestamp = Date.now();
		}
	},

	// Actualizar producto en el carrito
	updateProductInCart(state, { reservationId, cartItemId, updatedProduct }) {
		const cart = state.accountCarts.find((cart) => cart.id === reservationId);
		if (cart) {
			const index = cart.products.findIndex((p) => p.cartItemId === cartItemId);
			if (index !== -1) {
				cart.products[index] = {
					...cart.products[index],
					...updatedProduct,
					timestamp: Date.now(),
				};
			}
		}
	},

	// Limpiar carrito de una reserva
	clearCart(state, reservationId) {
		const cart = state.accountCarts.find((cart) => cart.id === reservationId);
		if (cart) {
			cart.products = [];
		}
	},

	// Eliminar carrito completo de una reserva
	removeCart(state, reservationId) {
		const index = state.accountCarts.findIndex((cart) => cart.id === reservationId);
		if (index !== -1) {
			state.accountCarts.splice(index, 1);
		}
	},

	// Establecer todos los carritos (para restaurar desde localStorage, etc.)
	setAccountCarts(state, carts) {
		state.accountCarts = carts;
	},

	// Mutations de overlays
	SET_IS_SHOW_OVERLAY_FEATURE_COMMANDER(state, payload) {
		state.isShowOverlayFeatureCommander = payload;
	},
	SET_IS_SHOW_OVERLAY_FEATURE_PRODUCT_COMMANDER(state, payload) {
		state.isShowOverlayFeatureProductCommander = payload;
	},
	SET_IS_SHOW_OVERLAY_FEATURE_CART(state, payload) {
		state.isShowOverlayFeatureCart = payload;
	},
	SET_IS_SHOW_OVERLAY_FEATURE_EDIT_PRODUCT_LIST(state, payload) {
		state.isShowOverlayFeatureEditProductList = payload;
	},
	SET_IS_SHOW_OVERLAY_FEATURE_BREAKFAST_COMMANDER(state, payload) {
		state.isShowOverlayFeatureBreakfastCommander = payload;
	},

	SET_IS_SHOW_OVERLAY_FEATURE_MENU_COMMANDER(state, payload) {
		state.isShowOverlayFeatureMenuCommander = payload;
	},

	// Producto en edición
	SET_EDITING_PRODUCT(state, product) {
		state.editingProduct = product;
	},

	// Si la edición viene del carrito
	SET_EDITING_FROM_CART(state, value) {
		state.editingFromCart = value;
	},
};

const actions = {
	// Inicializar carrito
	initializeCart({ commit }, reservationId) {
		commit("initializeCart", reservationId);
	},

	// Agregar producto
	addProduct({ commit }, { reservationId, product }) {
		commit("addProductToCart", { reservationId, product });
	},

	// Eliminar producto
	removeProduct({ commit }, { reservationId, cartItemId }) {
		commit("removeProductFromCart", { reservationId, cartItemId });
	},

	// Quitar una unidad del producto (o eliminar si llega al mínimo)
	subtractUnit({ commit }, { reservationId, cartItemId }) {
		commit("subtractUnit", { reservationId, cartItemId });
	},

	// Actualizar producto
	updateProduct({ commit }, { reservationId, cartItemId, updatedProduct }) {
		commit("updateProductInCart", { reservationId, cartItemId, updatedProduct });
	},

	// Limpiar carrito
	clearCart({ commit }, reservationId) {
		commit("clearCart", reservationId);
	},

	// Eliminar carrito
	removeCart({ commit }, reservationId) {
		commit("removeCart", reservationId);
	},

	// Actions de overlays
	setIsShowOverlayFeatureCommander({ commit }, payload) {
		commit("SET_IS_SHOW_OVERLAY_FEATURE_COMMANDER", payload);
	},
	setIsShowOverlayFeatureProductCommander({ commit }, payload) {
		commit("SET_IS_SHOW_OVERLAY_FEATURE_PRODUCT_COMMANDER", payload);
	},
	setIsShowOverlayFeatureCart({ commit }, payload) {
		commit("SET_IS_SHOW_OVERLAY_FEATURE_CART", payload);
	},
	setIsShowOverlayFeatureEditProductList({ commit }, payload) {
		commit("SET_IS_SHOW_OVERLAY_FEATURE_EDIT_PRODUCT_LIST", payload);
	},
	setIsShowOverlayFeatureBreakfastCommander({ commit }, payload) {
		commit("SET_IS_SHOW_OVERLAY_FEATURE_BREAKFAST_COMMANDER", payload);
	},

	setIsShowOverlayFeatureMenuCommander({ commit }, payload) {
		commit("SET_IS_SHOW_OVERLAY_FEATURE_MENU_COMMANDER", payload);
	},

	// Producto en edición
	setEditingProduct({ commit }, product) {
		commit("SET_EDITING_PRODUCT", product);
	},

	// Si la edición viene del carrito
	setEditingFromCart({ commit }, value) {
		commit("SET_EDITING_FROM_CART", value);
	},
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
};
